import { tools } from '@worktango/ai-assistant/react';
import { fetchCurrentAccountQuery } from '~/behaviors/react-query/account';
import { actionsQueries } from '~/behaviors/queries/actions.queries';
import { TransformedTask } from '~/behaviors/transformers/task.transformer';

export const getMyActionsListTool = tools.createTool({
  name: 'get_my_actions_list',
  description: 'Get the list of actions (tasks) for the current user',
  displayName: 'Getting your actions',
  pastTenseDisplayName: 'Fetched your actions',
  parameters: (t) =>
    t.Object({
      isCompleted: t.Optional(
        t.Boolean({
          description:
            'Whether to include completed actions in the list (true = only completed; false = only not completed; undefined = all)',
          default: undefined,
        }),
      ),
    }),
  fulfill: async ({ isCompleted }) => {
    try {
      const { account } = await fetchCurrentAccountQuery();

      if (!account || !account?.userId) {
        throw new Error('There was an error fetching the current user. Try again later, or contact support.');
      }

      if (!account?.companyId) {
        throw new Error(
          'The user is not associated with a company. Double check the Action Planning section, or contact support.',
        );
      }

      const actions = await actionsQueries.fetchCompanyActionsList(account?.companyId);

      if (!actions?.length) {
        throw new Error('There are no actions associated with this company.');
      }

      const shouldFilterByCompleted = isCompleted !== undefined;
      // See if the actions are associated with the user
      const userActions = actions.filter((action) =>
        action.users.find((user) => {
          const isAssignedToUser = user.id === account?.userId;
          if (!isAssignedToUser) {
            return false;
          }

          if (shouldFilterByCompleted) {
            return isCompleted ? action.isComplete : !action.isComplete;
          }

          return true;
        }),
      );

      if (!userActions?.length) {
        throw new Error('There are no actions associated with the user.');
      }

      return `Here are the actions for the user: ${formatActionsList(userActions)}`;
    } catch (error) {
      if (error instanceof Error) {
        return `Error: ${error.message}`;
      }
      return `Error: ${error}`;
    }
  },
});

export const formatActionsList = (actions: TransformedTask[]) => {
  return `
    Actions:
      ${actions.map((action) => {
        return `
        - Title: ${action.title}
        - Description: ${action.description}
        - Due Date: ${action.dueAt}
        - Surveys: ${action.surveys?.join(', ') || 'N/A'}
        - Categories: ${action.categories?.join(', ') || 'N/A'}
        - Is Completed: ${action.isComplete ? 'Yes' : 'No'}
        - Created At: ${action.createdAt}
        - Updated At: ${action.updatedAt}
        `;
      })}
    `;
};
